// UI shell (11-ui.md): top bar, live toolbar, viewport, dock trio, status bar with
// instructor hint, snap badges (UI-3), Measurements input with implicit focus (TL-4).
import { useEffect, useRef, useState } from "preact/hooks";
import type { AppApi } from "../appApi";
import type { ToolId } from "../tools/types";
import type { ToolManager } from "../tools/toolManager";
import type { Persistence } from "../io/persist";
import { openFileFromPicker, saveBlobToFile } from "../io/projectFile";
import { worldToScreen } from "../engine/camera";
import type { Bounds } from "../core/geometry";
import { useFocusTrap } from "./focusTrap";
import { loadUiPrefs, saveUiPrefs } from "./uiPrefs";
import { DOCK_DEFAULT, clampDockWidth, loadDockWidth, saveDockWidth } from "./dockWidth";
import { DockHandle } from "./dockHandle";
import { ContextMenu, type MenuState } from "./contextMenu";
import { LayersPanel } from "./layersPanel";
import { ObjectsPanel } from "./objectsPanel";
import { Inspector } from "./inspector";
import type { ProjectsScreen as ProjectsScreenType } from "./projectsScreen";
import type { EdenWatchUpdate } from "../eden";
import type { ShortcutsOverlay as ShortcutsOverlayType } from "./shortcutsOverlay";
import type { JobLinkDialog as JobLinkDialogType } from "./jobLinkDialog";
import { parseScalar, type Units } from "../core/units";
import { isLaunched } from "../eden/launch";
import { LibraryPanel } from "./libraryPanel";
import { SelectionPromptPanel } from "./selectionPrompt";
import type { DxfDialog as DxfDialogType } from "./dxfDialog";
import type { EdenConfig } from "../eden";
import type { ExportDialog as ExportDialogType } from "./exportDialog";
import type { SettingsDialog as SettingsDialogType } from "./settingsDialog";
import { buildSampleDoc } from "./sample";
import { Toasts, toasts } from "./toasts";
import { Icon, type IconName } from "./icons";

/** UI-6 coach marks: one-time two-liners on first activation of the key tools. */
const COACH: Partial<Record<string, string>> = {
  line: "Click points to draw. Type a length (like 2400) and press Enter for exact walls.",
  dimension: "Click two snapped points, move to pick a side, click to place. It follows the geometry.",
  calibrate: "Click two points a known distance apart, then type the real distance (like 820).",
  grid: "Click two corners for the grid area — or type cols,rows (like 6,4) before the second click.",
  freehand: "Press and drag to sketch. It auto-simplifies when you release.",
  text: "Click, type, Enter. Esc cancels the text. Font, size and weight live in the Inspector.",
  marker: "Cloud: click two opposite corners. Highlighter: drag — its size, colour and opacity are in the Inspector.",
  pipe: "Click two centre points. A click on a free fitting end attaches the pipe. Type a length and press Enter for an exact pipe.",
  wall: "Click the corners of the walls. Type a length (like 3000) and press Enter. Click the first point to close a room. Click on another wall to join it. Thickness and Room are in the Inspector.",
  tape: "Click two points to measure. Hold Ctrl on the second click to drop a construction guide."
};

/** Toolbar buttons; `sep` opens a new visual group (select · draw · pipe · annotate · measure). */
const TOOLS: { key: ToolId; icon: IconName; title: string; sep?: boolean }[] = [
  { key: "select", icon: "select", title: "Select (V)" },
  { key: "line", icon: "line", title: "Line (L)", sep: true },
  { key: "rect", icon: "rect", title: "Rectangle (R)" },
  { key: "circle", icon: "circle", title: "Circle (C)" },
  { key: "arc", icon: "arc", title: "Arc (A)" },
  { key: "freehand", icon: "freehand", title: "Freehand (F)" },
  // 3.16.2 (owner, 2026-10-20: "update the tool palette to allow selection of the pipe tool"): a group of its own
  { key: "pipe", icon: "pipe", title: "Pipe (P) — click two centre points, or a fitting end then a point; type a length ⏎", sep: true },
  // 3.20.0 (owner, 2026-10-21: "a wall drawing tool for easy layout of walls")
  { key: "wall", icon: "wall", title: "Wall — click the corners, type a length ⏎, click the start to close a room; a room by two corners in the Inspector" },
  { key: "text", icon: "text", title: "Text (T)", sep: true },
  { key: "label", icon: "label", title: "Label (N)" },
  { key: "dimension", icon: "dimension", title: "Dimension (D) — Alt-click a circle for ⌀" },
  { key: "grid", icon: "grid", title: "Grid (G)" },
  { key: "tape", icon: "tape", title: "Tape measure", sep: true },
  { key: "calibrate", icon: "calibrate", title: "Calibrate (K)" }
];

function MeasureInput({ tools }: { tools: ToolManager }) {
  const ref = useRef<HTMLInputElement>(null);
  const [invalid, setInvalid] = useState(false);
  const ui = tools.uiState();

  useEffect(() => {
    // TL-4 implicit focus: digits typed on the canvas route here.
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      // [data-own-keys]: a block that uses letters and digits itself (the Library filters)
      if (target?.closest("input, textarea, [contenteditable], [data-own-keys]")) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      // 3.20.0: during a drag, + and - scale a dragged image; nothing is typed then
      if (tools.dragging) return;
      const input = ref.current;
      if (!input) return;
      const starter = /^[0-9.\-<]$/.test(e.key);
      const continuation = input.value.length > 0 && /^[a-z0-9.,\-<'"*/+]$/i.test(e.key);
      if (starter || continuation) {
        input.focus();
        input.value += e.key;
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  const commit = () => {
    const input = ref.current;
    if (!input || input.value.trim() === "") return;
    if (tools.measure(input.value)) {
      input.value = "";
      input.blur();
    } else {
      // EC-DM-5: invalid flash, keep the text for correction
      setInvalid(true);
      setTimeout(() => setInvalid(false), 300);
    }
  };

  return (
    <input
      ref={ref}
      class={`measure-input${invalid ? " invalid" : ""}`}
      placeholder={ui.measureGhost || "measurements"}
      aria-label="Measurements"
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          commit();
          e.preventDefault();
        } else if (e.key === "Escape") {
          (e.currentTarget as HTMLInputElement).value = "";
          (e.currentTarget as HTMLInputElement).blur();
          e.preventDefault();
        }
        e.stopPropagation();
      }}
    />
  );
}

/**
 * DRAW BY NUMBERS — the number boxes for Line, Rectangle and Circle, over the top-left
 * of the drawing while one of those tools is on. Values are in the drawing's units and
 * take the same entries as the Inspector (3000, 3m, 3000mm). Enter in any box draws.
 */
function NumberPanel({ tools, units, precision }: { tools: ToolManager; units: Units; precision: number }) {
  const kind = tools.activeToolId;
  const [vals, setVals] = useState<Record<string, string>>({ x: "0", y: "0", angle: "0" });
  const [problem, setProblem] = useState<string | null>(null);
  if (kind !== "rect" && kind !== "line" && kind !== "circle") return null;

  const fields: Array<[string, string, string]> =
    kind === "rect"
      ? [["x", "X", "left edge"], ["y", "Y", "top edge"], ["w", "Width", ""], ["h", "Height", ""]]
      : kind === "line"
        ? [["x", "Start X", ""], ["y", "Start Y", ""], ["length", "Length", ""], ["angle", "Angle °", "0 right · 90 down"]]
        : [["x", "Centre X", ""], ["y", "Centre Y", ""], ["r", "Radius", ""]];

  const len = (k: string) => {
    const v = parseScalar(vals[k] ?? "", units);
    return v === null ? Number.NaN : v;
  };
  const draw = () => {
    const spec =
      kind === "rect"
        ? { kind, x: len("x"), y: len("y"), w: len("w"), h: len("h") }
        : kind === "line"
          ? { kind, x: len("x"), y: len("y"), length: len("length"), angleDeg: Number.parseFloat(vals.angle ?? "") }
          : { kind, x: len("x"), y: len("y"), r: len("r") };
    const why = tools.drawByNumbers(spec);
    setProblem(why);
    if (!why) toasts.push("success", "Drawn. Change it in the Inspector, or type new numbers and draw again.");
  };

  return (
    <div class="numpanel" role="group" aria-label="Draw by numbers">
      <span class="numpanel-title">{kind === "rect" ? "Rectangle" : kind === "line" ? "Line" : "Circle"} by numbers</span>
      {fields.map(([k, label, hint]) => (
        <label class="numpanel-field" key={k}>
          <span>{label}</span>
          <input
            id={`num-${kind}-${k}`}
            inputMode="decimal"
            value={vals[k] ?? ""}
            placeholder={hint || units}
            onInput={(e) => setVals({ ...vals, [k]: (e.target as HTMLInputElement).value })}
            onKeyDown={(e) => {
              e.stopPropagation(); // typing here is not a canvas shortcut
              if (e.key === "Enter") {
                e.preventDefault();
                draw();
              }
            }}
          />
        </label>
      ))}
      <button class="primary" onClick={draw}>
        Draw
      </button>
      <small class={problem ? "numpanel-note bad" : "numpanel-note"}>
        {problem ?? `${units} · X right, Y down · precision ${precision}`}
      </small>
    </div>
  );
}

function AppMenu({
  persist,
  onProjects,
  onExport,
  onSettings,
  onEdenSync,
  onEdenJobLink,
  onShortcuts
}: {
  persist: Persistence;
  onProjects: () => void;
  onExport: () => void;
  onSettings: () => void;
  onEdenSync: () => void;
  onEdenJobLink: () => void;
  onShortcuts: () => void;
}) {
  const [open, setOpen] = useState(false);
  const run = (fn: () => void | Promise<void>) => {
    setOpen(false);
    void fn();
  };
  const items: { label: string; fn: () => void | Promise<void> }[] = [
    { label: "New project", fn: () => persist.newProject() },
    { label: "Projects…", fn: onProjects },
    { label: "Export / Print… (Ctrl+E)", fn: onExport },
    {
      label: "Open file…",
      fn: async () => {
        const file = await openFileFromPicker(".tracecad,.json");
        if (!file) return;
        const errors = await persist.importFile(file);
        if (errors) toasts.push("error", `Import failed: ${errors[0]}`);
        else toasts.push("success", "Imported as a new project.");
      }
    },
    {
      label: "Save copy (.tracecad)",
      fn: async () => {
        const blob = await persist.exportFileBlob();
        await saveBlobToFile(blob, "project.tracecad");
      }
    },
    {
      label: "Export JSON",
      fn: async () => {
        await saveBlobToFile(persist.exportJsonBlob(), "project.json");
      }
    },
    { label: "Sync to Eden", fn: () => onEdenSync() },
    { label: "Link to Eden job…", fn: () => onEdenJobLink() },
    { label: "Settings…", fn: () => onSettings() },
    { label: "Keyboard shortcuts (?)", fn: () => onShortcuts() },
    { label: "Help…", fn: () => window.open(`${import.meta.env.BASE_URL}help.html`, "_blank") }
  ];
  return (
    <div class="appmenu">
      <button onClick={() => setOpen(!open)} aria-expanded={open} title="Menu" aria-label="Menu">
        <Icon name="menu" size={16} />
      </button>
      {open && (
        <>
          <div class="appmenu-backdrop" onClick={() => setOpen(false)} />
          <div class="context-menu appmenu-panel" role="menu">
            {items.map((item) => (
              <button key={item.label} role="menuitem" class="menu-item" onClick={() => run(item.fn)}>
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const STATUS_LABELS = { idle: "", saving: "Saving…", saved: "Saved", error: "Save failed — retrying" } as const;

/** EXP-1 custom region: one-shot drag-a-rectangle mode over the canvas. */
function RegionPicker({
  api,
  viewportEl,
  onDone,
  onCancel
}: {
  api: AppApi;
  viewportEl: HTMLElement;
  onDone: (b: Bounds) => void;
  onCancel: () => void;
}) {
  const [drag, setDrag] = useState<{ ax: number; ay: number; bx: number; by: number } | null>(null);
  // ref mirror: pointer events can arrive faster than re-renders — never trust closures
  const dragRef = useRef<typeof drag>(null);
  const update = (d: typeof drag) => {
    dragRef.current = d;
    setDrag(d);
  };
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const rect = viewportEl.getBoundingClientRect();
  const toWorld = (cx: number, cy: number) =>
    api.getViewport()!.cursorWorld({ x: cx - rect.left, y: cy - rect.top });
  return (
    <div
      ref={ref}
      class="region-picker"
      tabIndex={-1}
      style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
        e.stopPropagation();
      }}
      onPointerDown={(e) => {
        update({ ax: e.clientX, ay: e.clientY, bx: e.clientX, by: e.clientY });
        try {
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        } catch {
          /* synthetic pointers */
        }
      }}
      onPointerMove={(e) => {
        const d = dragRef.current;
        if (d) update({ ...d, bx: e.clientX, by: e.clientY });
      }}
      onPointerUp={() => {
        const drag = dragRef.current;
        if (!drag) return;
        if (Math.abs(drag.bx - drag.ax) < 6 || Math.abs(drag.by - drag.ay) < 6) {
          update(null); // too small — keep picking
          return;
        }
        const a = toWorld(drag.ax, drag.ay);
        const b = toWorld(drag.bx, drag.by);
        onDone({
          minX: Math.min(a.x, b.x),
          minY: Math.min(a.y, b.y),
          maxX: Math.max(a.x, b.x),
          maxY: Math.max(a.y, b.y)
        });
      }}
    >
      {drag && (
        <div
          class="region-rect"
          style={{
            left: Math.min(drag.ax, drag.bx) - rect.left,
            top: Math.min(drag.ay, drag.by) - rect.top,
            width: Math.abs(drag.bx - drag.ax),
            height: Math.abs(drag.by - drag.ay)
          }}
        />
      )}
      <div class="region-hint">Drag the rectangle to export · Esc = cancel</div>
    </div>
  );
}

/** One shared image/PDF picker (toolbar, starter card, Assets panel). */
function pickImages(api: AppApi): void {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*,.pdf,.dxf,.dwg";
  input.multiple = true;
  input.onchange = () => {
    if (input.files) void api.importFiles(input.files);
  };
  input.click();
}

/** Assets panel: every imported image asset, its footprint and where it's used. */
function AssetsPanel({ api }: { api: AppApi }) {
  const doc = api.store.doc;
  const hashes = Object.keys(doc.assets);
  const usage = (hash: string) =>
    Object.values(doc.entities).filter((e) => e.type === "image" && e.assetHash === hash).length;
  const fmtKB = (b: number) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
  if (hashes.length === 0) {
    return (
      <div>
        <small class="muted">No images yet — import a photo or plan to trace over.</small>
        <div class="insp-actions">
          <button onClick={() => pickImages(api)}>Insert image, PDF or DXF…</button>
        </div>
      </div>
    );
  }
  return (
    <div class="assets-panel">
      {hashes.map((hash) => {
        const a = doc.assets[hash]!;
        const used = usage(hash);
        return (
          <div key={hash} class="asset-row" title={`${a.mime} · sha256 ${hash.slice(0, 12)}…`}>
            <span class="object-icon">
              <Icon name="image" size={13} />
            </span>
            <span class="object-name">
              {a.w}×{a.h}px · {fmtKB(a.bytes)}
            </span>
            <span class="muted">{used === 0 ? "unused" : used === 1 ? "1 place" : `${used} places`}</span>
          </div>
        );
      })}
      <div class="insp-actions">
        <button onClick={() => pickImages(api)}>Insert image, PDF or DXF…</button>
      </div>
      <small class="muted">Image pixels live in this browser and in .tracecad files — they don't sync to Eden yet.</small>
    </div>
  );
}

/** UI-3: the zoom readout is a control — click for presets instead of wheel-scrubbing. */
function ZoomControl({ api, zoom }: { api: AppApi; zoom: number }) {
  const [open, setOpen] = useState(false);
  const apply = (pct: number | "fit") => {
    setOpen(false);
    const vp = api.getViewport();
    if (!vp) return;
    if (pct === "fit") vp.zoomExtents();
    else if (zoom > 0) vp.zoomBy(pct / zoom);
  };
  return (
    <span class="zoom-control">
      <button class="zoom" onClick={() => setOpen(!open)} aria-expanded={open} title="Zoom presets">
        {zoom >= 100 ? Math.round(zoom) : zoom.toFixed(1)}% <Icon name="chevron-down" size={10} />
      </button>
      {open && (
        <>
          <div class="appmenu-backdrop" onClick={() => setOpen(false)} />
          <div class="context-menu zoom-panel" role="menu">
            {[50, 100, 200, 400].map((pct) => (
              <button key={pct} role="menuitem" class="menu-item" onClick={() => apply(pct)}>
                {pct}%
              </button>
            ))}
            <button role="menuitem" class="menu-item" onClick={() => apply("fit")}>
              Fit drawing (Ctrl+0)
            </button>
          </div>
        </>
      )}
    </span>
  );
}

/** ANN-1: inline text editor overlaid at the entity's screen position (re-projected on camera moves). */
function InlineTextEditor({ tools, api, viewportEl }: { tools: ToolManager; api: AppApi; viewportEl: HTMLElement }) {
  const state = tools.textEdit!;
  const ref = useRef<HTMLTextAreaElement>(null);
  // UNCONTROLLED (2026-10-20): the box was `value={state.initial}`, so every redraw of the app (save status, Eden
  // chip, a toast, the mouse coordinates) put the starting text back and cleared what had been typed. The starting
  // text is set once, here; after that the box keeps what is typed.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.value = state.initial;
    el.focus();
    el.select();
  }, []);
  const rect = viewportEl.getBoundingClientRect();
  const vp = api.getViewport();
  const screen = vp
    ? worldToScreen(vp.camera.current, vp.scheduler.width, vp.scheduler.height, state.world)
    : state.screen;
  const fontPx = vp ? Math.max(11, state.fontMm * vp.camera.current.scale) : state.fontPx;
  const commit = () => tools.finishTextEdit(ref.current?.value ?? "");
  return (
    <textarea
      ref={ref}
      class="inline-text-editor"
      style={{
        left: rect.left + screen.x,
        top: rect.top + screen.y - fontPx * 0.2,
        fontSize: `${fontPx}px`,
        height: state.multiline ? undefined : `${fontPx * 1.6}px`
      }}
      rows={state.multiline ? 3 : 1}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") {
          tools.finishTextEdit(null);
          e.preventDefault();
        } else if (e.key === "Enter" && (!state.multiline || e.ctrlKey)) {
          commit();
          e.preventDefault();
        }
      }}
      onBlur={commit}
    />
  );
}

export function App({ api }: { api: AppApi }) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState("x 0.0  y 0.0");
  const [zoom, setZoom] = useState(100);
  const [tools, setTools] = useState<ToolManager | null>(null);
  const [persist, setPersist] = useState<Persistence | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [showProjects, setShowProjects] = useState(false);
  // lazy modals (bundle gate): each dialog ships in its own chunk, loaded on open
  const [ProjectsScreenComp, setProjectsScreenComp] = useState<typeof ProjectsScreenType | null>(null);
  const [ExportDialogComp, setExportDialogComp] = useState<typeof ExportDialogType | null>(null);
  const [SettingsDialogComp, setSettingsDialogComp] = useState<typeof SettingsDialogType | null>(null);
  const [ShortcutsOverlayComp, setShortcutsOverlayComp] = useState<typeof ShortcutsOverlayType | null>(null);
  const [JobLinkDialogComp, setJobLinkDialogComp] = useState<typeof JobLinkDialogType | null>(null);
  // DXF import: a lazy dialog, opened by the importFiles façade through a window event.
  const [DxfDialogComp, setDxfDialogComp] = useState<typeof DxfDialogType | null>(null);
  const [dxfFiles, setDxfFiles] = useState<File[] | null>(null);
  useEffect(() => {
    const onDxf = (e: Event) => {
      const files = (e as CustomEvent<File[]>).detail;
      if (!files?.length) return;
      void import("./dxfDialog").then((m) => {
        setDxfDialogComp(() => m.DxfDialog);
        setDxfFiles(files);
      });
    };
    window.addEventListener("tracecad:dxf", onDxf);
    return () => window.removeEventListener("tracecad:dxf", onDxf);
  }, []);
  const [jobLinkCfg, setJobLinkCfg] = useState<EdenConfig | null>(null);
  type JobPreset = { label: string; jobId: string; tenderId?: string; refs?: Record<string, string> };
  const [jobLinkPreset, setJobLinkPreset] = useState<JobPreset | undefined>(undefined);
  const openJobLink = (cfg: EdenConfig, preset?: JobPreset) => {
    void import("./jobLinkDialog").then((m) => {
      setJobLinkDialogComp(() => m.JobLinkDialog);
      setJobLinkPreset(preset);
      setJobLinkCfg(cfg);
    });
  };

  // The Eden host of each drawing started from Eden, kept for this browser session so a
  // closed dialog can be reopened from the top-bar chip with the job still fixed.
  const presetFor = (docId: string): JobPreset | undefined => {
    try {
      const raw = sessionStorage.getItem(`tracecad.edenPreset.${docId}`);
      return raw ? (JSON.parse(raw) as JobPreset) : undefined;
    } catch {
      return undefined;
    }
  };
  const openLinkForCurrent = async () => {
    const eden = await import("../eden");
    const cfg = await eden.loadEdenConfig();
    if (!cfg) {
      toasts.push("info", "Eden is not connected here. Open the drawing from the job in Eden, or paste the config in Settings.");
      return;
    }
    openJobLink(cfg, presetFor(api.store.doc.id));
  };

  // Started from a job or tender in Eden (main.tsx): ask only for the drawing number.
  useEffect(() => {
    const onNew = (e: Event) => {
      const preset = (e as CustomEvent<JobPreset>).detail;
      try {
        sessionStorage.setItem(`tracecad.edenPreset.${api.store.doc.id}`, JSON.stringify(preset));
      } catch {
        /* no session storage: the dialog still opens now */
      }
      void (async () => {
        const eden = await import("../eden");
        const cfg = await eden.loadEdenConfig();
        if (!cfg) {
          toasts.push("error", "Opened from Eden, but the sign-in did not arrive. Go back to Eden and press New drawing again.");
          return;
        }
        openJobLink(cfg, preset);
      })();
    };
    window.addEventListener("tracecad:eden-new", onNew);
    return () => window.removeEventListener("tracecad:eden-new", onNew);
  }, []);
  const openProjects = () => {
    void import("./projectsScreen").then((m) => {
      setProjectsScreenComp(() => m.ProjectsScreen);
      setShowProjects(true);
    });
  };
  const openExport = () => {
    void import("./exportDialog").then((m) => {
      setExportDialogComp(() => m.ExportDialog);
      setShowExport(true);
    });
  };
  const openSettings = () => {
    void import("./settingsDialog").then((m) => {
      setSettingsDialogComp(() => m.SettingsDialog);
      setShowSettings(true);
    });
  };
  const openShortcuts = () => {
    // deterministic toggle: if it's on screen, close it — no stale-closure races
    if (document.querySelector(".shortcuts-modal")) {
      setShowShortcuts(false);
      return;
    }
    void import("./shortcutsOverlay").then((m) => {
      setShortcutsOverlayComp(() => m.ShortcutsOverlay);
      setShowShortcuts(true);
    });
  };
  const [showExport, setShowExport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showStarter, setShowStarter] = useState(false);
  const [customRegion, setCustomRegion] = useState<Bounds | null>(null);
  const [pickingRegion, setPickingRegion] = useState(false);
  const [coach, setCoach] = useState<{ tool: string; text: string } | null>(null);
  const [edenWatch, setEdenWatch] = useState<EdenWatchUpdate | null>(null);
  const [uiLayout, setUiLayout] = useState(loadUiPrefs);
  const starterRef = useRef<HTMLDivElement>(null);
  const [, forceRender] = useState(0);
  const bump = () => forceRender((n) => n + 1);

  const isCollapsed = (id: string) => uiLayout.collapsed[id] ?? id === "assets";
  const togglePanel = (id: string) => {
    const next = { ...uiLayout, collapsed: { ...uiLayout.collapsed, [id]: !isCollapsed(id) } };
    setUiLayout(next);
    saveUiPrefs(next);
  };
  // The dock width: the width the person chose is kept; the width shown is that, limited to the window.
  const appRef = useRef<HTMLDivElement>(null);
  const [dockWanted, setDockWanted] = useState(() => loadDockWidth());
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const dockWidth = clampDockWidth(dockWanted, windowWidth);
  /** Show a width at once, with no render: the drawing column follows through the CSS variable. */
  const previewDock = (w: number) => appRef.current?.style.setProperty("--dock-w", `${w}px`);
  const commitDock = (w: number) => {
    const v = clampDockWidth(w, window.innerWidth);
    setDockWanted(v);
    saveDockWidth(v);
    previewDock(v);
  };

  useFocusTrap(starterRef, showStarter);

  // Eden sync (lazy module — brief §5): user-triggered, never blocks drawing
  const edenSync = async () => {
    const eden = await import("../eden");
    const cfg = await eden.loadEdenConfig();
    if (!cfg) {
      toasts.push("info", "Eden sync isn't configured — add eden.json or paste the config in Settings.");
      openSettings();
      return;
    }
    toasts.push("info", "Syncing to Eden…", false);
    // The person asked: a large drawing (3.17.0) does not wait for its once-a-minute turn.
    const result = await eden.pushProject(cfg, api.store.doc, { force: true });
    if (result.needsJob) {
      toasts.push("info", "Pick the job this drawing belongs to.");
      openJobLink(cfg); // job-mode account: link first, the dialog's Save syncs
      bump();
      return;
    }
    toasts.push(result.ok ? (result.skipped ? "info" : "success") : "error", result.message);
    bump(); // refresh the status chip
  };

  // Job mode (Ozcrete): link or re-link the open drawing to a job / new revision
  const edenJobLink = async () => {
    const eden = await import("../eden");
    const cfg = await eden.loadEdenConfig();
    if (!cfg) {
      toasts.push("info", "Eden sync isn't configured — paste the config in Settings → Eden sync.");
      openSettings();
      return;
    }
    try {
      const tids = await eden.ensureTids(cfg);
      if (tids.mode !== "job") {
        toasts.push("info", "This Eden account keeps drawings as Projects — use Menu → Sync to Eden.");
        return;
      }
    } catch (e) {
      toasts.push("error", `Eden: ${(e as Error).message}`);
      return;
    }
    openJobLink(cfg, presetFor(api.store.doc.id));
  };

  // status chip (reads the sync module's localStorage state — no eager Eden code)
  const edenStatus = (() => {
    try {
      const s = JSON.parse(localStorage.getItem("tracecad.edenSync") ?? "null") as {
        lastStatus?: { ok: boolean; message: string } | null;
      } | null;
      return s?.lastStatus ?? null;
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    const container = viewportRef.current;
    if (!container) return;
    const { viewport, tools: manager, persist: persistence } = api.mountViewport(container);
    viewport.events.onCursor = (w) => setCoords(`x ${w.x.toFixed(1)}  y ${w.y.toFixed(1)}`);
    viewport.events.onCamera = (_cam, percent) => {
      setZoom(percent);
      if (manager.textEdit) bump(); // keep the inline editor pinned to its world position
      persistence.saveViewStateSoon();
    };
    viewport.onGuidesChanged = () => persistence.saveViewStateSoon();
    manager.onContextMenu = (screen, items) => {
      const r = container.getBoundingClientRect();
      setMenu({ x: r.left + screen.x, y: r.top + screen.y, items });
    };
    setTools(manager);
    setPersist(persistence);

    // UI-6 first-run: starter overlay when there's nothing but this fresh empty doc
    void persistence.listProjects().then((list) => {
      // Not when opened from Eden: the drawing is already chosen, and the welcome card
      // would sit on top of the job dialog and the drawing (found 2026-09-30).
      if (isLaunched()) return;
      if (list.length <= 1 && Object.keys(api.store.doc.entities).length === 0) setShowStarter(true);
    });

    // UI-6 coach marks: one-time tip on first activation of key tools
    let lastTool = manager.activeToolId;
    const coachUnsub = manager.subscribe(() => {
      const id = manager.activeToolId;
      if (id !== lastTool) {
        lastTool = id;
        const text = COACH[id];
        if (text && !manager.prefs.coachDone.includes(id)) setCoach({ tool: id, text });
        else setCoach(null);
      }
    });

    // IMG-2: drag-drop onto the canvas + Ctrl+V paste of images
    const onDragOver = (e: DragEvent) => e.preventDefault();
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer?.files?.length) void api.importFiles(e.dataTransfer.files);
    };
    const onPaste = (e: ClipboardEvent) => {
      if ((e.target as HTMLElement | null)?.closest("input, textarea, [contenteditable]")) return;
      const files = [...(e.clipboardData?.files ?? [])].filter(
        (f) => f.type.startsWith("image/") || f.type === "application/pdf"
      );
      if (files.length > 0) {
        e.preventDefault();
        void api.importFiles(files);
      } else if (e.clipboardData) {
        manager.setHint("Nothing to paste"); // EC-IMG-6, hint not toast
      }
    };
    container.addEventListener("dragover", onDragOver);
    container.addEventListener("drop", onDrop);
    window.addEventListener("paste", onPaste);

    // Ctrl+E opens the export dialog (EXP-1); ? opens the shortcuts overlay
    const onExportKey = (e: KeyboardEvent) => {
      const inField = (e.target as HTMLElement | null)?.closest?.("input, textarea, [contenteditable]");
      if (e.ctrlKey && !e.shiftKey && e.key.toLowerCase() === "e") {
        if (!inField) {
          openExport();
          e.preventDefault();
          e.stopPropagation();
        }
      } else if (e.key === "?" && !e.ctrlKey && !inField) {
        openShortcuts();
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onExportKey, true);

    // delete confirmation: counts only matter for multi-deletes (singles are visibly obvious). A step that also adds
    // parts draws an object again (a pump size, a butterfly valve, 3.19.0): nothing is gone, so no message.
    const deleteToastUnsub = api.store.subscribe((_doc, cmd) => {
      const del =
        cmd?.kind === "deleteEntities"
          ? cmd
          : cmd?.kind === "batch" && !cmd.commands.some((c) => c.kind === "addEntities")
            ? cmd.commands.find((c) => c.kind === "deleteEntities")
            : undefined;
      if (del?.kind === "deleteEntities" && del.removed.length > 1) {
        toasts.push("info", `Deleted ${del.removed.length} objects`);
      }
    });

    // Is Eden configured at all? (local override or a deployed eden.json — no eager
    // Eden code loads here.)
    const edenConfigured = async (): Promise<boolean> => {
      if (localStorage.getItem("tracecad.eden") || localStorage.getItem("tracecad.edenSync")) return true;
      try {
        return (await fetch(`${import.meta.env.BASE_URL}eden.json`, { cache: "no-store" })).ok;
      } catch {
        return false;
      }
    };

    // E5 open-in-editor: register TraceCAD as an editor every start (independent of the
    // live-updates toggle) so Eden shows "Open in TraceCAD" and origin changes self-heal.
    void (async () => {
      if (!(await edenConfigured())) return;
      const eden = await import("../eden");
      const cfg = await eden.loadEdenConfig();
      if (cfg) await eden.registerEditor(cfg);
    })();

    // S3 live updates: the Eden→app event bridge (polls on focus + every 90s while
    // visible; a browser PWA can't receive Eden's signed webhooks directly). Started
    // only when Eden is actually configured — the module stays lazy otherwise.
    let stopEdenWatch: (() => void) | null = null;
    void (async () => {
      if (!manager.prefs.edenLiveUpdates) return;
      if (!(await edenConfigured())) return;
      const eden = await import("../eden");
      stopEdenWatch = eden.startEdenWatch({
        current: () => ({ id: api.store.doc.id, modifiedAt: api.store.doc.modifiedAt }),
        listLocal: async () =>
          (await persistence.listProjects()).map((m) => ({ id: m.id, modifiedAt: m.modifiedAt })),
        onUpdate: (u) => {
          setEdenWatch(u);
          if (u.isNews) {
            toasts.push("info", "This project has a newer version on Eden — Menu → Projects… → Load to fetch it.");
          }
        }
      });
    })();

    // S2 auto-sync: after autosave settles AND the toggle is on, quietly push to Eden.
    // Debounced well past the autosave cadence; checksum skip makes idle repeats free.
    let autoSyncTimer: ReturnType<typeof setTimeout> | null = null;
    // Opened from Eden (a live launch key): saving goes to Eden without the toggle, and
    // sooner — the person saves here and goes back to Eden to open it again.
    let savedToEdenOnce = false;
    // 3.17.0: a large drawing goes to Eden as a file, at most once a minute while the person draws. The push says when
    // the next copy may go (deferred); this timer sends it then, with the drawing as it is at that time.
    let edenLaterTimer: ReturnType<typeof setTimeout> | null = null;
    let edenInFlight = false;
    let edenInFlightSince = 0;
    let lastEdenError = "";
    const autoPush = async (force: boolean): Promise<void> => {
      try {
        const eden = await import("../eden");
        const cfg = await eden.loadEdenConfig();
        if (!cfg) return;
        if (edenLaterTimer) clearTimeout(edenLaterTimer);
        edenLaterTimer = null;
        edenInFlight = true;
        edenInFlightSince = Date.now();
        const result = await eden.pushProject(cfg, api.store.doc, { force }); // status chip only
        edenInFlight = false;
        if (result.deferred) {
          edenLaterTimer = setTimeout(() => void autoPush(false), (result.retryInMs ?? 60_000) + 250);
        } else if (!result.ok && /^(UPLOAD_FAILED|TOO_LARGE)/.test(result.message)) {
          // Say it once, in plain words; the drawing on this computer is safe. A failed upload is tried again.
          if (result.message !== lastEdenError) toasts.push("error", result.message);
          lastEdenError = result.message;
          if (result.message.startsWith("UPLOAD_FAILED")) edenLaterTimer = setTimeout(() => void autoPush(false), 60_000);
        } else if (result.ok) {
          lastEdenError = "";
        }
        if (isLaunched() && result.ok && !result.skipped && !savedToEdenOnce) {
          savedToEdenOnce = true;
          toasts.push("success", "Saved to Eden. It is on the job there; later saves go to Eden too.");
        }
        bump();
      } catch (err) {
        edenInFlight = false;
        console.warn("tracecad: auto-sync failed", err);
      }
    };
    const autoSyncUnsub = api.saveStatus.subscribe(() => {
      const launched = isLaunched();
      if (api.saveStatus.get() !== "saved" || !(manager.prefs.edenAutoSync || launched)) return;
      if (autoSyncTimer) clearTimeout(autoSyncTimer);
      autoSyncTimer = setTimeout(() => void autoPush(false), launched ? 4_000 : 30_000);
    });
    // Leaving the page or the tab: a large drawing that waits for its turn goes to Eden now. A hidden tab finishes the
    // upload; a closed tab cannot, so the browser asks before it closes while a copy is not yet in Eden.
    const pushWaitingNow = (): void => {
      if (edenLaterTimer && !edenInFlight) void autoPush(true);
    };
    const onHidden = (): void => {
      if (document.visibilityState === "hidden") pushWaitingNow();
    };
    const onLeave = (e: BeforeUnloadEvent): void => {
      // A push that runs longer than 1.5 s is an upload of a large drawing (a small drawing takes well under a second).
      if (edenLaterTimer || (edenInFlight && Date.now() - edenInFlightSince > 1500)) {
        pushWaitingNow();
        e.preventDefault();
      }
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", pushWaitingNow);
    window.addEventListener("beforeunload", onLeave);

    const unsubs = [
      api.store.subscribe(bump),
      manager.subscribe(bump),
      api.saveStatus.subscribe(bump),
      coachUnsub,
      deleteToastUnsub,
      autoSyncUnsub,
      () => {
        if (autoSyncTimer) clearTimeout(autoSyncTimer);
        if (edenLaterTimer) clearTimeout(edenLaterTimer);
        document.removeEventListener("visibilitychange", onHidden);
        window.removeEventListener("pagehide", pushWaitingNow);
        window.removeEventListener("beforeunload", onLeave);
        stopEdenWatch?.();
      }
    ];
    return () => {
      unsubs.forEach((fn) => fn());
      container.removeEventListener("dragover", onDragOver);
      container.removeEventListener("drop", onDrop);
      window.removeEventListener("paste", onPaste);
      window.removeEventListener("keydown", onExportKey, true);
      viewport.destroy();
    };
  }, [api]);

  const { store } = api;
  const ui = tools?.uiState();

  const status = api.saveStatus.get();
  return (
    <div class="app" ref={appRef} style={`--dock-w:${dockWidth}px`}>
      <header class="topbar">
        {persist && (
          <AppMenu
            persist={persist}
            onProjects={openProjects}
            onExport={openExport}
            onSettings={openSettings}
            onEdenSync={() => void edenSync()}
            onEdenJobLink={() => void edenJobLink()}
            onShortcuts={openShortcuts}
          />
        )}
        <span class="name">TraceCAD</span>
        <span class="muted">{store.doc.name}</span>
        <span
          class={`save-dot ${status}`}
          title={STATUS_LABELS[status] || "Autosave"}
          aria-label={STATUS_LABELS[status] || "Autosave idle"}
        />
        {STATUS_LABELS[status] && <span class="save-label muted">{STATUS_LABELS[status]}</span>}
        {edenWatch?.currentRemoteNewer ? (
          <span
            class="eden-chip newer"
            title="A newer version of this project exists on the Eden server — Menu → Projects… → Load"
          >
            Eden: remote newer
          </span>
        ) : (
          edenStatus && edenStatus.message === "needs a drawing number" ? (
            // Waiting, not failed: one click opens the dialog that finishes the link.
            <button
              class="eden-chip waiting"
              title="This drawing is not on a job yet. Click to give it a drawing number and save it to the job."
              onClick={() => void openLinkForCurrent()}
            >
              Eden: give it a drawing number ›
            </button>
          ) : edenStatus && (
            <span
              class={`eden-chip${edenStatus.ok ? "" : " error"}`}
              title="Eden sync status — Menu → Sync to Eden"
            >
              Eden: {edenStatus.message}
              {edenWatch && edenWatch.remoteNewerCount > 0 ? ` · ${edenWatch.remoteNewerCount} behind` : ""}
            </span>
          )
        )}
        <div class="spacer" />
        <button
          disabled={!store.canUndo}
          onClick={() => store.undo()}
          title={store.canUndo ? `Undo ${store.undoLabel()} (Ctrl+Z)` : "Nothing to undo"}
        >
          <Icon name="undo" size={14} /> Undo
        </button>
        <button
          disabled={!store.canRedo}
          onClick={() => store.redo()}
          title={store.canRedo ? `Redo ${store.redoLabel()} (Ctrl+Y)` : "Nothing to redo"}
        >
          <Icon name="redo" size={14} /> Redo
        </button>
        <button onClick={() => api.getViewport()?.zoomExtents()} title="Zoom extents (Ctrl+0)">
          <Icon name="fit" size={14} /> Fit
        </button>
      </header>

      <nav class="toolbar" aria-label="Tools">
        {TOOLS.map((t) => (
          <>
            {t.sep && <div class="toolbar-sep" />}
            <button
              key={t.key}
              title={t.title}
              aria-label={t.title}
              class={ui?.toolId === t.key ? "active" : ""}
              aria-pressed={ui?.toolId === t.key}
              onClick={() => tools?.switchTool(t.key)}
            >
              <Icon name={t.icon} size={18} />
            </button>
          </>
        ))}
        <div class="toolbar-sep" />
        <button
          title="Revision cloud — mark up an area"
          aria-label="Revision cloud"
          class={ui?.toolId === "marker" && tools?.markerKind === "cloud" ? "active" : ""}
          onClick={() => tools?.startMarker("cloud")}
        >
          <Icon name="cloud" size={18} />
        </button>
        <button
          title="Highlighter — translucent markup stroke"
          aria-label="Highlighter"
          class={ui?.toolId === "marker" && tools?.markerKind === "highlight" ? "active" : ""}
          onClick={() => tools?.startMarker("highlight")}
        >
          <Icon name="highlighter" size={18} />
        </button>
        <div class="toolbar-sep" />
        <button
          title="Insert image or PDF (I) — or drag-drop / paste"
          aria-label="Insert image or PDF"
          onClick={() => pickImages(api)}
        >
          <Icon name="image" size={18} />
        </button>
      </nav>

      <div class="viewport" ref={viewportRef} />
      {tools && ui?.prompt && <SelectionPromptPanel key={ui.prompt} api={api} tools={tools} kind={ui.prompt} />}
      {tools && !ui?.prompt && <NumberPanel tools={tools} units={store.doc.units} precision={store.doc.precision} />}

      <aside class="dock" aria-label="Panels">
        <DockHandle
          width={dockWidth}
          windowWidth={windowWidth}
          onPreview={previewDock}
          onCommit={commitDock}
          onReset={() => commitDock(DOCK_DEFAULT)}
        />
        <div class="dock-scroll">
        {(
          [
            ["inspector", "Inspector"],
            ["objects", "Objects"],
            ["layers", "Layers"],
            ["assets", "Assets"],
            ["library", "Library"]
          ] as const
        ).map(([id, label]) => (
          <section class="panel" key={id}>
            <button
              class={`panel-header${isCollapsed(id) ? " collapsed" : ""}`}
              aria-expanded={!isCollapsed(id)}
              onClick={() => togglePanel(id)}
            >
              <span class="panel-chevron">
                <Icon name="chevron-down" size={11} />
              </span>
              {label}
            </button>
            {!isCollapsed(id) && (
              <div class="panel-body">
                {id === "inspector" && tools && <Inspector api={api} tools={tools} />}
                {id === "objects" && tools && <ObjectsPanel api={api} tools={tools} />}
                {id === "layers" && tools && <LayersPanel api={api} tools={tools} />}
                {id === "assets" && <AssetsPanel api={api} />}
                {id === "library" && tools && <LibraryPanel tools={tools} />}
              </div>
            )}
          </section>
        ))}
        </div>
      </aside>

      {menu && <ContextMenu state={menu} onClose={() => setMenu(null)} />}
      {tools?.textEdit && viewportRef.current && (
        <InlineTextEditor key={tools.textEditId} tools={tools} api={api} viewportEl={viewportRef.current} />
      )}
      {showProjects && persist && ProjectsScreenComp && (
        <ProjectsScreenComp persist={persist} onClose={() => setShowProjects(false)} />
      )}
      {showExport && ExportDialogComp && (
        <ExportDialogComp
          api={api}
          onClose={() => setShowExport(false)}
          customRegion={customRegion}
          defaultRegionMode={customRegion ? "custom" : "extents"}
          onPickRegion={() => {
            setShowExport(false);
            setPickingRegion(true);
          }}
        />
      )}
      {pickingRegion && viewportRef.current && (
        <RegionPicker
          api={api}
          viewportEl={viewportRef.current}
          onDone={(b) => {
            setCustomRegion(b);
            setPickingRegion(false);
            openExport();
          }}
          onCancel={() => {
            setPickingRegion(false);
            openExport();
          }}
        />
      )}
      {showSettings && tools && SettingsDialogComp && (
        <SettingsDialogComp api={api} tools={tools} onClose={() => setShowSettings(false)} />
      )}
      {showShortcuts && tools && ShortcutsOverlayComp && (
        <ShortcutsOverlayComp tools={tools} onClose={() => setShowShortcuts(false)} />
      )}
      {dxfFiles && tools && DxfDialogComp && (
        <DxfDialogComp api={api} tools={tools} files={dxfFiles} onClose={() => setDxfFiles(null)} />
      )}
      {jobLinkCfg && JobLinkDialogComp && (
        <JobLinkDialogComp
          cfg={jobLinkCfg}
          docId={api.store.doc.id}
          docName={api.store.doc.name}
          preset={jobLinkPreset}
          onClose={() => setJobLinkCfg(null)}
          onSaved={() => {
            setJobLinkCfg(null);
            void edenSync();
          }}
        />
      )}

      {showStarter && persist && (
        <div class="modal-backdrop">
          <div
            class="starter-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Welcome"
            ref={starterRef}
            onKeyDown={(e) => {
              if (e.key === "Escape") setShowStarter(false);
            }}
          >
            <h2>Welcome to TraceCAD</h2>
            <div class="starter-cards">
              <button
                class="starter-card"
                onClick={() => {
                  setShowStarter(false);
                  pickImages(api);
                }}
              >
                <span class="starter-icon">
                  <Icon name="image" size={30} />
                </span>
                <b>Trace a photo or plan</b>
                <small>Import, straighten the perspective, calibrate to real size, draw over it.</small>
              </button>
              <button class="starter-card" onClick={() => setShowStarter(false)}>
                <span class="starter-icon">
                  <Icon name="file" size={30} />
                </span>
                <b>Start blank</b>
                <small>Draw with real units — type 2400 for an exact wall.</small>
              </button>
              <button
                class="starter-card"
                onClick={() => {
                  setShowStarter(false);
                  void persist.adoptDoc(buildSampleDoc()).then(() => api.getViewport()?.zoomExtents());
                }}
              >
                <span class="starter-icon">
                  <Icon name="sample" size={30} />
                </span>
                <b>Open the sample</b>
                <small>A finished mini-plan — poke at the dimensions and walls.</small>
              </button>
            </div>
            <small class="muted starter-help">
              Press <kbd>?</kbd> any time for shortcuts ·{" "}
              <a href={`${import.meta.env.BASE_URL}help.html`} target="_blank" rel="noreferrer">
                full help &amp; instructions
              </a>
            </small>
          </div>
        </div>
      )}

      {coach && tools && (
        <div class="coach-mark">
          <span>{coach.text}</span>
          <button
            onClick={() => {
              tools.setPref({ coachDone: [...tools.prefs.coachDone, coach.tool] });
              setCoach(null);
            }}
          >
            Got it
          </button>
        </div>
      )}
      {!api.persistent && (
        <div class="storage-banner">Changes won't be saved in this browser mode — use “Save copy” to keep your work.</div>
      )}
      <Toasts />

      <footer class="statusbar">
        <span class="hint">{ui?.hint ?? ""}</span>
        {ui && tools && (
          <span class="badges">
            <button
              class={`badge${ui.snapEnabled ? " on" : ""}`}
              onClick={() => tools.toggleSnap()}
              title="Object snap on/off (Alt = suspend)"
            >
              SNAP
            </button>
            <button
              class={`badge${ui.orthoSticky ? " on" : ""}`}
              onClick={() => tools.toggleOrtho()}
              title="Ortho lock (or hold Shift)"
            >
              ORTHO
            </button>
            <button
              class={`badge${ui.gridSnap ? " on" : ""}`}
              onClick={() => tools.toggleGridSnap()}
              title="Snap to canvas grid"
            >
              GRID
            </button>
          </span>
        )}
        <span class="snap-kind">{ui?.snapKind ?? ""}</span>
        {tools &&
          (() => {
            const layer = store.doc.layers.find((l) => l.id === tools.activeLayerId);
            return layer ? (
              <span class="active-layer-chip" title="Active layer — new objects land here">
                <span class="chip-swatch" style={{ background: layer.color }} />
                {layer.name}
              </span>
            ) : null;
          })()}
        {tools && <MeasureInput tools={tools} />}
        <span class="coords">{coords}</span>
        <ZoomControl api={api} zoom={zoom} />
      </footer>
    </div>
  );
}
